// ============================================================
// fileKinds - the asset kinds kept as FILES, one row each (stage 3).
//
// A map or a scene is text the library stores in versioned documents; a
// picture or a PDF is bytes, stored through the files port beside a version
// document (`assetFiles/v0007`). What a picked file IS comes from the same
// test the handouts drawer uses, so the library and the drawer never
// disagree about a file: a picture is an image, a PDF is a handout. Pure.
// ============================================================
import { kindOf as handoutKindOf } from '../handouts/kinds';
import { IMAGE_TYPES, PDF_TYPE } from '../handouts/types';
import type { FileKind } from './types';

/** What every kind shows, text or file: the chip, the card, and whether a session may hold a copy. */
export interface KindFacts {
  /** The card: "Image". */
  label: string;
  /** The chip: "Images". */
  plural: string;
  copiesIntoSession: boolean;
  /** What opening it is called on the details panel ("Open in Build", "View"); null for a kind that does not open. */
  open: string | null;
}

export const FILE_ROWS: { [K in FileKind]: KindFacts } = {
  image: { label: 'Image', plural: 'Images', copiesIntoSession: true, open: 'View' },
  handout: { label: 'Handout', plural: 'Handouts', copiesIntoSession: true, open: 'View' },
};

/** The file types the library takes: the handouts drawer's. */
export const FILE_TYPES: readonly string[] = [...IMAGE_TYPES, PDF_TYPE];

/** The drawer's word for a file, as the library's kind. */
const BY_HANDOUT_KIND: Partial<Record<string, FileKind>> = { image: 'image', pdf: 'handout' };

/** Which file kind a picked file is; null for a file the text kinds must read. */
export function fileKindOf(file: { type: string; name: string }): FileKind | null {
  return BY_HANDOUT_KIND[handoutKindOf(file) ?? ''] ?? null;
}

/**
 * What the page says where files cannot go. A Firebase project needs Storage
 * turned on, and this one is on the free plan with no bucket (the user's
 * ruling: refuse with a sentence until it is).
 */
export const NO_FILES =
  'Images and handouts need file storage, which this server has not turned on. They work on a PocketBase server.';

const EXTENSIONS: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'application/pdf': 'pdf',
};

/** The extension of a type the library and the handouts take; null for any other. */
export function extensionOf(contentType: string): string | null {
  return EXTENSIONS[contentType] ?? null;
}

/** The extension a stored file goes back to the computer with. */
export function extensionFor(contentType: string): string {
  return extensionOf(contentType) ?? 'bin';
}
